<div class="acs-page">
  <!-- Page Header -->
  <div class="acs-page-header">
    <div class="acs-page-header__title-section">
      <h1 class="acs-page-header__title">
        📊 Portal Access Logs
      </h1>
      <p class="acs-page-header__subtitle">
        Comprehensive audit trail of user access to the Chapter Portal with Okta authentication data
      </p>
      <div class="acs-page-header__stats">
        <span class="acs-stat-pill acs-stat-pill--active">
          🟢 {{ getActiveSessionsCount() }} Active Sessions
        </span>
        <span class="acs-stat-pill acs-stat-pill--total">
          📋 {{ accessLogs.length }} Access Events
        </span>
        <span class="acs-stat-pill acs-stat-pill--users">
          👥 {{ getUniqueUsersCount() }} Unique Users
        </span>
      </div>
    </div>
  </div>

  <!-- Date Range Filters -->
  <div class="filter-section">
    <div class="filter-header">
      <h3 class="filter-title">📅 Date Range Filter</h3>
      <p class="filter-description">Select a time range to view access logs</p>
    </div>

    <div class="date-range-filters">
      <!-- Quick Filter Buttons -->
      <div class="quick-filters">
        <button
          *ngFor="let preset of dateRangePresets"
          class="quick-filter-btn"
          [class.quick-filter-btn--active]="selectedDateRange === preset.days && !useCustomDateRange"
          (click)="selectDateRange(preset.days)">
          {{ preset.label }}
        </button>
      </div>

      <!-- Custom Date Range -->
      <div class="custom-date-range">
        <div class="custom-date-inputs">
          <div class="date-input-group">
            <label for="startDate">From:</label>
            <input
              type="date"
              id="startDate"
              [(ngModel)]="customStartDate"
              (change)="onCustomDateRangeChange()"
              class="date-input">
          </div>
          <div class="date-input-group">
            <label for="endDate">To:</label>
            <input
              type="date"
              id="endDate"
              [(ngModel)]="customEndDate"
              (change)="onCustomDateRangeChange()"
              class="date-input">
          </div>
          <button class="refresh-btn" (click)="loadAccessLogs()" title="Refresh data">
            🔄 Refresh
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Advanced Filters Section -->
  <div class="filter-section">
    <div class="filter-header">
      <h3 class="filter-title">
        🔍 Advanced Filters
        <button class="toggle-filters-btn" (click)="toggleAdvancedFilters()">
          {{ showAdvancedFilters ? '▼' : '▶' }}
        </button>
      </h3>
    </div>

    <div class="advanced-filters" *ngIf="showAdvancedFilters">
      <div class="advanced-filter-grid">
        <!-- Session Status Filter -->
        <div class="filter-group">
          <label>Session Status:</label>
          <select [(ngModel)]="filterBySessionStatus" (change)="onFilterChange()" class="filter-select">
            <option value="all">All Sessions</option>
            <option value="Active">Active Only</option>
            <option value="Ended">Ended Only</option>
            <option value="Expired">Expired Only</option>
          </select>
        </div>

        <!-- Impersonation Filter -->
        <div class="filter-group">
          <label>Impersonation:</label>
          <select [(ngModel)]="filterByImpersonation" (change)="onFilterChange()" class="filter-select">
            <option value="all">All Sessions</option>
            <option value="impersonation">Impersonation Only</option>
            <option value="normal">Normal Sessions Only</option>
          </select>
        </div>

        <!-- IP Warning Filter -->
        <div class="filter-group">
          <label class="checkbox-label">
            <input
              type="checkbox"
              [(ngModel)]="filterByIPWarning"
              (change)="onFilterChange()">
            <span>⚠️ Show only users with multiple IPs</span>
          </label>
        </div>

        <!-- Clear Filters Button -->
        <div class="filter-group">
          <button class="clear-filters-btn" (click)="clearAdvancedFilters()">
            Clear All Filters
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Active User Filter Display -->
  <div class="active-filter-banner" *ngIf="selectedUserFilter">
    <div class="active-filter-content">
      <span class="active-filter-icon">👤</span>
      <span class="active-filter-text">
        Showing access logs for: <strong>{{ getFilteredUserName() }}</strong>
      </span>
      <button class="clear-filter-btn" (click)="clearUserFilter()">
        ✕ Clear Filter
      </button>
    </div>
  </div>

  <!-- Access Logs Section -->
  <div class="acs-section access-logs-section">
    <div class="acs-section__header">
      <h2 class="acs-section__title">
        📋 Access Log Entries
        <span class="acs-section__badge" *ngIf="accessLogs.length > 0">
          {{ accessLogs.length }}
        </span>
      </h2>
      <p class="acs-section__description">
        Complete log of all user access events including login, logout, and session expiry with Okta authentication data, IP addresses, and session information. Click on a user name to view all their access history.
      </p>
    </div>

    <div class="acs-section__content">
      <acs-data-grid
        *ngIf="!loadingLogs"
        [data]="accessLogs"
        [columns]="accessLogColumns"
        [loading]="loadingLogs"
        [searchable]="true"
        [sortable]="true"
        [enableDropdownFilters]="true"
        [showPagination]="true"
        [pageSize]="20"
        searchPlaceholder="Search access logs..."
        emptyMessage="No access logs found for the selected date range. Try adjusting the date range or filters."
        (cellClick)="onCellClick($event)">
      </acs-data-grid>

      <!-- Loading state for access logs -->
      <div *ngIf="loadingLogs" class="acs-loading-container">
        <acs-loading-spinner message="Loading access logs..."></acs-loading-spinner>
      </div>
    </div>
  </div>
</div>

<!-- Enhanced Okta Claims Modal -->
<acs-modal
  *ngIf="showClaimsModal && selectedLog"
  [isOpen]="showClaimsModal"
  title="Access Event Details"
  size="large"
  (closeModal)="closeClaimsModal()">
  <div class="claims-modal">
    <!-- Event Overview -->
    <div class="claims-section">
      <h3 class="claims-section-title">Event Overview</h3>
      <div class="claims-table">
        <div class="claims-table-row">
          <span class="claims-table-label">Event Type:</span>
          <span class="claims-table-value" [innerHTML]="getEventBadge(selectedLog.eventType)"></span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Session Status:</span>
          <span class="claims-table-value" [innerHTML]="getSessionStatusBadge(selectedLog.sessionStatus)"></span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Timestamp:</span>
          <span class="claims-table-value">{{ formatTimestamp(selectedLog.timestamp) }}</span>
        </div>
        <div class="claims-table-row" *ngIf="selectedLog.sessionDuration">
          <span class="claims-table-label">Session Duration:</span>
          <span class="claims-table-value">{{ formatDuration(selectedLog.sessionDuration) }}</span>
        </div>
        <div class="claims-table-row" *ngIf="selectedLog.impersonationSessionId">
          <span class="claims-table-label">Impersonation Session:</span>
          <span class="claims-table-value claims-table-value--monospace">{{ selectedLog.impersonationSessionId }}</span>
        </div>
      </div>
    </div>

    <!-- User Information -->
    <div class="claims-section">
      <h3 class="claims-section-title">User Information</h3>
      <div class="claims-table">
        <div class="claims-table-row">
          <span class="claims-table-label">User Name:</span>
          <span class="claims-table-value">{{ selectedLog.userName }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Email:</span>
          <span class="claims-table-value">{{ selectedLog.email }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Role:</span>
          <span class="claims-table-value">{{ selectedLog.userRole || 'N/A' }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Person ID:</span>
          <span class="claims-table-value">{{ selectedLog.personId || 'N/A' }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Aura Unique ID:</span>
          <span class="claims-table-value claims-table-value--monospace">{{ selectedLog.auraUniqueId || 'N/A' }}</span>
        </div>
      </div>
    </div>

    <!-- Technical Information -->
    <div class="claims-section">
      <h3 class="claims-section-title">Technical Information</h3>
      <div class="claims-table">
        <div class="claims-table-row">
          <span class="claims-table-label">IP Address:</span>
          <span class="claims-table-value">
            {{ selectedLog.clientIP || 'N/A' }}
            <span *ngIf="selectedLog.ipWarning" class="ip-warning-badge" title="User has logged in from multiple IP addresses">
              ⚠️ Multiple IPs Detected
            </span>
          </span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Browser:</span>
          <span class="claims-table-value">{{ selectedLog.browserInfo }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">Operating System:</span>
          <span class="claims-table-value">{{ selectedLog.osInfo }}</span>
        </div>
        <div class="claims-table-row">
          <span class="claims-table-label">User Agent:</span>
          <span class="claims-table-value claims-table-value--wrap claims-table-value--monospace">{{ selectedLog.userAgent || 'N/A' }}</span>
        </div>
      </div>
    </div>

    <!-- Okta Token Claims -->
    <div class="claims-section" *ngIf="parseOktaClaims(selectedLog.oktaTokenClaims)">
      <h3 class="claims-section-title">Okta Token Claims</h3>
      <div class="claims-table">
        <ng-container *ngIf="parseOktaClaims(selectedLog.oktaTokenClaims) as claims">
          <div class="claims-table-row" *ngFor="let claim of claims | keyvalue">
            <span class="claims-table-label">{{ claim.key }}:</span>
            <span class="claims-table-value claims-table-value--monospace">
              {{ claim.value | json }}
            </span>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- No Claims Message -->
    <div class="claims-section" *ngIf="!parseOktaClaims(selectedLog.oktaTokenClaims)">
      <p class="no-claims-message">No Okta token claims available for this event (typically occurs on logout or session expiry)</p>
    </div>
  </div>
</acs-modal>
